import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import { andonApi } from "~/redux/warehouse/andon";
import { workstationApi } from "~/redux/warehouse/workstation";
import { SystemMode } from "~/types/api";

import {
  AndonViewOption,
  WorkstationPongData,
  AndonStatus,
  PongData,
  AndonEventWithMeta,
  AndonDisplayLine,
  AndonBoardLayoutItem
} from "./andon.types";
import { calculateAndonPortKey } from "./andon.utils";

/** Default per-workstation history cap. User can adjust via pong controls. */
export const DEFAULT_MAX_HISTORY_ENTRIES = 200;

export interface AndonState {
  portStatusByPortKey: Record<string, AndonStatus>;
  handRaisedByWorkstationId: Record<
    string,
    {
      raised: boolean;
      timestamp: string | null;
      requestType?: string | null;
    }
  >;
  portHasBinsByPortKey: Record<string, number[]>;
  gridNameById: Record<string, string>;
  gridSystemModeById: Record<string, SystemMode | null>;
  currentView: AndonViewOption;
  eventExplorerIds: string[];
  boardLayout: AndonBoardLayoutItem[];
  boardDimensions: {
    columns: number;
    rows: number;
  };
  boardScale: number;
  boardIconScale: number;
  boardDisplayLines: AndonDisplayLine[];
  editModeEnabled: boolean;
  selectedAndonLayoutName: string | null;
  eventHistory: AndonEventWithMeta[];
  eventSaveLimit: number;
  fetchStatusEndpointsInterval: number;
  workstationsPongs: Record<string, WorkstationPongData>;
  workstationPongHistory: Record<
    string,
    {
      pongData: PongData;
      loopTime: number;
      timestamp: string;
    }[]
  >;
  autoPingInterval: number;
  autoPingEnabled: boolean;
  pongHistoryTimeWindow: number;
  maxHistoryEntries: number;
  currentBatchPongId: string | null;
  boardPortToWorkstationId: Record<string, string>;
}

export const initialState: AndonState = {
  portStatusByPortKey: {},
  handRaisedByWorkstationId: {},
  portHasBinsByPortKey: {},
  gridNameById: {},
  gridSystemModeById: {},
  currentView: "default",
  boardLayout: [],
  eventExplorerIds: [],
  boardDimensions: {
    columns: 5,
    rows: 3
  },
  boardScale: 1.0,
  boardIconScale: 1.0,
  boardDisplayLines: ["icon", "status", "deviceId", "userName"],
  editModeEnabled: false,
  eventSaveLimit: 0,
  fetchStatusEndpointsInterval: 5000,
  workstationsPongs: {},
  workstationPongHistory: {},
  autoPingInterval: 30000,
  pongHistoryTimeWindow: 10 * 1000, // default: 10 seconds
  maxHistoryEntries: DEFAULT_MAX_HISTORY_ENTRIES,
  autoPingEnabled: false,
  eventHistory: [],
  selectedAndonLayoutName: null,
  currentBatchPongId: null,
  boardPortToWorkstationId: {}
};

export const andonSlice = createSlice({
  name: "andon",
  initialState,
  reducers: {
    setAndonViewOption(
      state: AndonState,
      action: PayloadAction<AndonViewOption>
    ) {
      state.currentView = action.payload;
    },
    setSelectedAndonLayoutName(
      state: AndonState,
      action: PayloadAction<string | null>
    ) {
      state.selectedAndonLayoutName = action.payload;
    },
    setEventExplorerIds(state: AndonState, action: PayloadAction<string[]>) {
      state.eventExplorerIds = action.payload;
    },
    setBoardLayout(
      state: AndonState,
      action: PayloadAction<AndonBoardLayoutItem[]>
    ) {
      state.boardLayout = action.payload;
    },
    setBoardDimensions(
      state: AndonState,
      action: PayloadAction<{ columns: number; rows: number }>
    ) {
      state.boardDimensions = action.payload;
    },
    setBoardScale(state: AndonState, action: PayloadAction<number>) {
      state.boardScale = action.payload;
    },
    setBoardIconScale(state: AndonState, action: PayloadAction<number>) {
      state.boardIconScale = action.payload;
    },
    setBoardDisplayLines(
      state: AndonState,
      action: PayloadAction<AndonDisplayLine[]>
    ) {
      state.boardDisplayLines = action.payload;
    },
    setBoardPortToWorkstationId(
      state: AndonState,
      action: PayloadAction<Record<string, string>>
    ) {
      state.boardPortToWorkstationId = action.payload;
    },
    clearBoardPortToWorkstationId(state: AndonState) {
      state.boardPortToWorkstationId = {};
    },
    setEditModeEnabled(state: AndonState, action: PayloadAction<boolean>) {
      state.editModeEnabled = action.payload;
    },
    setPortStatus(
      state,
      action: PayloadAction<{
        portId: string;
        status: AndonStatus;
        gridId?: string;
      }>
    ) {
      const { portId, status, gridId } = action.payload;
      const key = gridId ? calculateAndonPortKey(gridId, portId) : portId;
      state.portStatusByPortKey[key] = status;
    },
    setPortHasBins(
      state,
      action: PayloadAction<{
        portId: string;
        bins: number[];
        gridId?: string;
      }>
    ) {
      const { portId, bins, gridId } = action.payload;
      const key = gridId ? calculateAndonPortKey(gridId, portId) : portId;
      state.portHasBinsByPortKey[key] = bins;
    },
    clearAndonWorkstationsAndGrids(state: AndonState) {
      state.portStatusByPortKey = {};
      state.handRaisedByWorkstationId = {};
      state.portHasBinsByPortKey = {};
      state.gridSystemModeById = {};
    },
    saveEventLimitData(state: AndonState, action: PayloadAction<number>) {
      state.eventSaveLimit = action.payload;
    },
    setFetchStatusEndpointsInterval(
      state: AndonState,
      action: PayloadAction<number>
    ) {
      state.fetchStatusEndpointsInterval = action.payload;
    },
    setPongHistoryTimeWindow(state: AndonState, action: PayloadAction<number>) {
      state.pongHistoryTimeWindow = action.payload;
    },
    setAutoPingInterval(state: AndonState, action: PayloadAction<number>) {
      state.autoPingInterval = action.payload;
    },
    setAutoPingEnabled(state: AndonState, action: PayloadAction<boolean>) {
      state.autoPingEnabled = action.payload;
    },
    clearPongHistory(state: AndonState) {
      state.workstationPongHistory = {};
      state.workstationsPongs = {};
    },
    setHandRaised(
      state: AndonState,
      action: PayloadAction<{
        workstationId: string;
        handRaised: boolean;
        timestamp?: string;
        requestType?: string;
      }>
    ) {
      const { workstationId, handRaised, requestType } = action.payload;
      state.handRaisedByWorkstationId[workstationId] = {
        raised: handRaised,
        timestamp: action.payload.timestamp ?? null,
        requestType: handRaised ? (requestType ?? null) : null
      };
    },
    setGridSystemMode(
      state: AndonState,
      action: PayloadAction<{ gridId: string; systemMode: SystemMode }>
    ) {
      const { gridId, systemMode } = action.payload;
      state.gridSystemModeById[gridId] = systemMode;
    },
    setWorkstationPong(
      state: AndonState,
      action: PayloadAction<{
        workstationId: string;
        pongData: PongData;
        pongLoopTime: number;
        batchTimestamp: string | null;
        timePingSent: string;
      }>
    ) {
      state.workstationsPongs[action.payload.workstationId] = {
        pongData: action.payload.pongData,
        pongLoopTime: action.payload.pongLoopTime,
        batchTimestamp: action.payload.batchTimestamp,
        timePingSent: action.payload.timePingSent
      };
    },
    setCurrentPongId(state: AndonState, action: PayloadAction<string | null>) {
      state.currentBatchPongId = action.payload;
    },
    addPongHistoryEntry: (
      state,
      action: PayloadAction<{
        workstationId: string;
        pongData: PongData;
        loopTime: number;
      }>
    ) => {
      const { workstationId, pongData, loopTime } = action.payload;
      const existing = state.workstationPongHistory[workstationId] ?? [];

      const limit = state.maxHistoryEntries || DEFAULT_MAX_HISTORY_ENTRIES;
      const updated = [
        ...existing,
        {
          pongData,
          loopTime,
          timestamp: new Date().toISOString()
        }
      ].slice(-limit);

      state.workstationPongHistory[workstationId] = updated;
    },
    addEventToHistory: (state, action: PayloadAction<AndonEventWithMeta>) => {
      if (state.eventSaveLimit === 0) return;

      if (state.eventHistory.length >= state.eventSaveLimit) {
        state.eventHistory.splice(0, 1);
      }

      state.eventHistory.push(action.payload);
    },
    clearEventData(state: AndonState) {
      state.eventHistory = [];
    },
    clearAndonSessionData(state: AndonState) {
      state.workstationsPongs = {};
      state.workstationPongHistory = {};
      state.portStatusByPortKey = {};
      state.eventHistory = [];
      state.boardPortToWorkstationId = {};
    },
    removeWorkstationPong(state, action: PayloadAction<string>) {
      const { [action.payload]: _, ...rest } = state.workstationsPongs;
      state.workstationsPongs = rest;
    },
    setMaxHistoryEntries(state: AndonState, action: PayloadAction<number>) {
      state.maxHistoryEntries = action.payload;
      const trimmed: AndonState["workstationPongHistory"] = {};
      for (const [wsId, entries] of Object.entries(
        state.workstationPongHistory
      )) {
        const sliced = entries.slice(-action.payload);
        if (sliced.length > 0) {
          trimmed[wsId] = sliced;
        }
      }
      state.workstationPongHistory = trimmed;
    }
  },
  selectors: {
    selectPortStatusByPortKeyMap: (state: AndonState) =>
      state.portStatusByPortKey,
    selectPortBinsByPortKeyMap: (state: AndonState) =>
      state.portHasBinsByPortKey,
    selectGridNameByIdMap: (state: AndonState) => state.gridNameById,
    selectGridModeByIdMap: (state: AndonState) => state.gridSystemModeById,
    selectHandRaisedByIdMap: (state: AndonState) =>
      state.handRaisedByWorkstationId,
    selectAndonEventSaveLimit: (state: AndonState) => state.eventSaveLimit,
    selectSelectedAndonLayoutName: (state: AndonState) =>
      state.selectedAndonLayoutName,
    selectEventExplorerIds: (state: AndonState) => state.eventExplorerIds,
    selectAndonBoardLayout: (state: AndonState) => state.boardLayout,
    selectAndonBoardDimensions: (state: AndonState) => state.boardDimensions,
    selectAndonBoardScale: (state: AndonState) => state.boardScale,
    selectAndonBoardIconScale: (state: AndonState) => state.boardIconScale,
    selectAndonBoardDisplayLines: (state: AndonState) =>
      state.boardDisplayLines,
    selectCurrentAndonBoardView: (state: AndonState) => state.currentView,
    selectEditModeEnabled: (state: AndonState) => state.editModeEnabled,
    selectFetchStatusEndpointsInterval: (state: AndonState) =>
      state.fetchStatusEndpointsInterval,
    selectIsAndonBoardFullScreen: (state: AndonState) =>
      state.currentView === "board fullscreen",
    selectPongs: (state: AndonState) => state.workstationsPongs,
    selectAllPongHistories: (state: AndonState) => state.workstationPongHistory,
    selectPongHistoryTimeWindow: (state: AndonState) =>
      state.pongHistoryTimeWindow,
    selectAutoPingInterval: (state: AndonState) => state.autoPingInterval,
    selectAutoPingEnabled: (state: AndonState) => state.autoPingEnabled,
    selectMaxHistoryEntries: (state: AndonState) =>
      state.maxHistoryEntries || DEFAULT_MAX_HISTORY_ENTRIES,
    selectBoardPortToWorkstationId: (state: AndonState) =>
      state.boardPortToWorkstationId
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      workstationApi.endpoints.getWorkstations.matchFulfilled,
      (state, { payload: workstations }) => {
        const incomingWsIds = new Set<string>();

        for (const workstation of workstations) {
          incomingWsIds.add(workstation.id);
          state.gridNameById[workstation.autostoreGridId] =
            workstation.autostoreGridName;
          state.gridSystemModeById[workstation.autostoreGridId] ??= null;

          for (const port of workstation.ports) {
            const key = calculateAndonPortKey(
              workstation.autostoreGridId,
              port.portId
            );
            state.portHasBinsByPortKey[key] ??= [];
          }
        }

        for (const wsId of incomingWsIds) {
          state.handRaisedByWorkstationId[wsId] ??= {
            raised: false,
            timestamp: null
          };
        }
      }
    );
    builder.addMatcher(
      andonApi.endpoints.getAndonState.matchFulfilled,
      (state, { payload: andonState }) => {
        for (const workstation of andonState.workstations) {
          state.handRaisedByWorkstationId[workstation.workstationId] = {
            raised: workstation.handRaised,
            timestamp: workstation.handRaised ? workstation.lastModified : null,
            requestType: workstation.handRaised
              ? (workstation.requestType ?? null)
              : null
          };
        }
      }
    );
    builder.addMatcher(
      andonApi.endpoints.getAndonWorkstations.matchFulfilled,
      (state, { payload }) => {
        // Hydrate grid names and hand-raise state from the snapshot; seed
        // grid modes and port bins as placeholders for incoming andon event
        // data.
        for (const workstation of payload.workstations) {
          state.gridNameById[workstation.autostoreGridId] =
            workstation.autostoreGridName;
          state.gridSystemModeById[workstation.autostoreGridId] ??= null;

          for (const port of workstation.ports) {
            const key = calculateAndonPortKey(
              workstation.autostoreGridId,
              port.portId
            );
            state.portHasBinsByPortKey[key] ??= [];
          }

          state.handRaisedByWorkstationId[workstation.workstationId] = {
            raised: workstation.handRaised,
            // handRaisedAt is typed as Date but arrives as an ISO string.
            timestamp:
              workstation.handRaised && workstation.handRaisedAt
                ? new Date(workstation.handRaisedAt).toISOString()
                : null,
            requestType: workstation.handRaised
              ? (workstation.handRaiseRequestType ?? null)
              : null
          };
        }
      }
    );
  }
});

export const {
  setPortStatus,
  setPortHasBins,
  setAndonViewOption,
  setEventExplorerIds,
  setBoardLayout,
  setBoardDimensions,
  setBoardScale,
  setBoardIconScale,
  setBoardDisplayLines,
  setBoardPortToWorkstationId,
  clearBoardPortToWorkstationId,
  setEditModeEnabled,
  clearAndonWorkstationsAndGrids,
  saveEventLimitData,
  setFetchStatusEndpointsInterval,
  setHandRaised,
  clearEventData,
  clearAndonSessionData,
  setWorkstationPong,
  addPongHistoryEntry,
  setAutoPingInterval,
  setPongHistoryTimeWindow,
  setAutoPingEnabled,
  clearPongHistory,
  setGridSystemMode,
  addEventToHistory,
  setSelectedAndonLayoutName,
  removeWorkstationPong,
  setCurrentPongId,
  setMaxHistoryEntries
} = andonSlice.actions;

export const {
  selectPortStatusByPortKeyMap,
  selectHandRaisedByIdMap,
  selectPortBinsByPortKeyMap,
  selectGridNameByIdMap,
  selectGridModeByIdMap,
  selectAndonEventSaveLimit,
  selectEventExplorerIds,
  selectAndonBoardLayout,
  selectAndonBoardDimensions,
  selectAndonBoardScale,
  selectAndonBoardIconScale,
  selectAndonBoardDisplayLines,
  selectCurrentAndonBoardView,
  selectEditModeEnabled,
  selectFetchStatusEndpointsInterval,
  selectIsAndonBoardFullScreen,
  selectPongs,
  selectAllPongHistories,
  selectAutoPingInterval,
  selectPongHistoryTimeWindow,
  selectAutoPingEnabled,
  selectSelectedAndonLayoutName,
  selectMaxHistoryEntries,
  selectBoardPortToWorkstationId
} = andonSlice.selectors;

const andonPersistConfig = {
  key: "andon",
  storage,
  whitelist: [
    "currentView",
    "eventExplorerIds",
    "boardLayout",
    "boardDimensions",
    "editModeEnabled",
    "fetchStatusEndpointsInterval",
    "workstationsPongs",
    "workstationPongHistory",
    "autoPingInterval",
    "pongHistoryTimeWindow",
    "maxHistoryEntries",
    "autoPingEnabled",
    "portStatusByPortKey",
    "selectedAndonLayoutName",
    "eventHistory",
    "eventSaveLimit"
  ]
};

export const andonReducer = persistReducer(
  andonPersistConfig,
  andonSlice.reducer
);
